import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Layers, TrendingDown, TrendingUp, ArrowRight, Search, Calculator,
  AlertTriangle, ArrowUpDown, BookOpen,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import {
  fmtPct, fmtMoney, fmtAum, fmtDate, TotalReturnCell,
} from '@/components/coveredCall/ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Covered-Call / Option-Income ETF hub — /covered-call-etfs
 *
 * The money angle: every other site quotes the headline distribution
 * yield. We show the 1-year TOTAL return (price change + every
 * distribution actually paid), which is the number that decides
 * whether a 130% "yield" made you money or just handed back your own
 * capital. Data is read-only from stock_cache via
 * /api/covered-call/funds.
 */
const COLUMNS = [
  { key: 'ticker', label: 'Fund', align: 'left' },
  { key: 'dividend_yield', label: 'Yield', hint: 'Headline distribution yield' },
  { key: 'dist_ttm', label: 'Paid 12mo', hint: 'Dollars per share distributed in the last 12 months' },
  { key: 'frequency', label: 'Pays', align: 'center' },
  { key: 'expense_ratio', label: 'Fee', hint: 'Annual expense ratio' },
  { key: 'nav_change_1y', label: '1Y Price', hint: 'Share-price change — the NAV erosion column' },
  { key: 'total_return_1y', label: '1Y Total Return', hint: 'Price change + distributions received' },
  { key: 'net_assets', label: 'Size' },
];

export default function CoveredCallHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [family, setFamily] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'net_assets', dir: 'desc' });
  const [showAllFamilies, setShowAllFamilies] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/covered-call/funds`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  useSeoMeta({
    title: 'Covered Call ETF List — yields vs true total return (YieldMax, JEPI, JEPQ) | DiviDrip',
    description: 'Every covered-call and option-income ETF ranked by what it actually returned: distribution yield, dollars paid, expense ratio, NAV erosion and 1-year total return. Updated daily.',
    canonical: 'https://twylightcrow.com/covered-call-etfs',
  });

  const funds = data?.funds || [];
  const asOf = useMemo(() => {
    const dates = funds.map((f) => f.as_of).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : null;
  }, [funds]);

  const stats = useMemo(() => {
    const withTr = funds.filter((f) => f.total_return_1y != null);
    const losers = withTr.filter((f) => f.total_return_1y < 0);
    const eroding = funds.filter((f) => f.nav_change_1y != null && f.nav_change_1y <= -25);
    const yields = funds.map((f) => f.dividend_yield).filter((v) => v != null).sort((a, b) => a - b);
    return {
      total: funds.length,
      scored: withTr.length,
      losers: losers.length,
      eroding: eroding.length,
      medianYield: yields.length ? yields[Math.floor(yields.length / 2)] : null,
    };
  }, [funds]);

  const familyCounts = useMemo(() => {
    const m = new Map();
    funds.forEach((f) => m.set(f.family, (m.get(f.family) || 0) + 1));
    return [...m.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [funds]);

  const rows = useMemo(() => {
    const needle = q.trim().toUpperCase();
    let out = funds.filter((f) => {
      if (family !== 'all' && f.family !== family) return false;
      if (!needle) return true;
      return f.ticker.includes(needle) || (f.name || '').toUpperCase().includes(needle);
    });
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av || '').localeCompare(String(bv || '')) * mult;
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * mult;
    });
    return out;
  }, [funds, family, q, sort]);

  // JSON-LD: FAQ (rich results) + ItemList of the 10 biggest funds.
  useEffect(() => {
    if (!funds.length) return;
    const blocks = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: HUB_FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Covered call and option income ETFs',
        itemListElement: funds.slice(0, 10).map((f, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `${f.ticker} — ${f.name}`,
        })),
      },
    ];
    const nodes = blocks.map((b, i) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(b);
      s.setAttribute('data-testid', `cc-hub-jsonld-${i}`);
      document.head.appendChild(s);
      return s;
    });
    return () => { nodes.forEach((n) => n.remove()); };
  }, [funds]);

  const toggleSort = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: key === 'ticker' ? 'asc' : 'desc' }
  ));

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="cc-hub-page">
      <BackOrHomeButton />

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400 font-semibold">
        <Layers className="w-3.5 h-3.5" /> Option income research
      </div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-slate-100">
        Covered call ETFs, ranked by what they <span className="text-cyan-600 dark:text-cyan-400">actually returned</span>
      </h1>
      <p className="mt-4 max-w-3xl text-base text-slate-600 dark:text-slate-400 leading-relaxed">
        A 130% distribution yield is not a 130% return. This table pairs every fund's headline yield
        with its share-price change and its <strong className="text-slate-900 dark:text-slate-100">true
        1-year total return</strong> — price movement plus every distribution actually paid — so you can
        see which option-income funds compounded and which ones handed you your own capital back with a fee attached.
      </p>

      {loading && (
        <div className="mt-8 space-y-3 animate-pulse" data-testid="cc-hub-loading">
          {[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded-lg bg-slate-200 dark:bg-slate-800" />)}
        </div>
      )}

      {error && (
        <div className="mt-8 p-6 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-800 dark:text-amber-300" data-testid="cc-hub-error">
          Fund data is temporarily unavailable. Please refresh in a moment.
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Headline stats */}
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cc-hub-stats">
            <StatTile label="Funds tracked" value={stats.total} testId="cc-hub-stat-total" />
            <StatTile label="Median headline yield" value={fmtPct(stats.medianYield)} testId="cc-hub-stat-median" />
            <StatTile
              label="Lost money over 12mo"
              value={stats.scored ? `${stats.losers} of ${stats.scored}` : '—'}
              tone="danger"
              hint={stats.total > stats.scored ? `${stats.total - stats.scored} funds are younger than a year` : null}
              testId="cc-hub-stat-losers"
            />
            <StatTile
              label="Price down 25%+"
              value={stats.total ? `${stats.eroding} of ${stats.total}` : '—'}
              tone="warn"
              testId="cc-hub-stat-eroding"
            />
          </div>

          {/* Popular head-to-heads */}
          {!!data.pairs?.length && (
            <section className="mt-12" data-testid="cc-hub-pairs">
              <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">Popular head-to-heads</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Side-by-side pages with the total-return math worked out.
              </p>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.pairs.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/${p.slug}`}
                    className="group flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors"
                    data-testid={`cc-hub-pair-${p.slug}`}
                  >
                    <span className="font-mono font-bold text-slate-900 dark:text-slate-100">
                      {p.a} <span className="text-slate-400 font-sans font-normal text-xs px-1">vs</span> {p.b}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ))}
                <Link
                  to="/learn/jepi-vs-jepq"
                  className="group flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-cyan-300 dark:border-cyan-800 bg-cyan-50/60 dark:bg-cyan-950/20 hover:border-cyan-500 transition-colors"
                  data-testid="cc-hub-pair-jepi-vs-jepq"
                >
                  <span className="font-mono font-bold text-slate-900 dark:text-slate-100">
                    JEPI <span className="text-slate-400 font-sans font-normal text-xs px-1">vs</span> JEPQ
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 dark:text-cyan-400">Full guide</span>
                </Link>
              </div>
            </section>
          )}

          {/* Filters */}
          <section className="mt-12" data-testid="cc-hub-table-section">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
                  Every option-income ETF we track
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {rows.length} of {stats.total} funds · click any column to sort
                </p>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter ticker or name…"
                  className="pl-9 pr-3 py-2 w-64 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  data-testid="cc-hub-search"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2" data-testid="cc-hub-family-filters">
              <FamilyChip label="All families" active={family === 'all'} onClick={() => setFamily('all')} testId="cc-hub-family-all" />
              {(showAllFamilies ? familyCounts : familyCounts.slice(0, 12)).map((f) => (
                <FamilyChip
                  key={f.name}
                  label={`${f.name} (${f.count})`}
                  active={family === f.name}
                  onClick={() => setFamily(f.name)}
                  testId={`cc-hub-family-${f.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                />
              ))}
              {familyCounts.length > 12 && (
                <button
                  onClick={() => setShowAllFamilies((v) => !v)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold text-cyan-700 dark:text-cyan-400 hover:underline"
                  data-testid="cc-hub-family-toggle"
                >
                  {showAllFamilies ? 'Show fewer' : `+${familyCounts.length - 12} more`}
                </button>
              )}
            </div>

            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
              <table className="w-full text-sm" data-testid="cc-hub-table">
                <thead className="bg-slate-50 dark:bg-slate-900/60">
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        title={c.hint}
                        onClick={() => toggleSort(c.key)}
                        className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 cursor-pointer select-none hover:text-cyan-600 dark:hover:text-cyan-400 whitespace-nowrap ${c.align === 'left' ? 'text-left' : c.align === 'center' ? 'text-center' : 'text-right'}`}
                        data-testid={`cc-hub-th-${c.key}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {c.label}
                          {sort.key === c.key && <ArrowUpDown className="w-3 h-3 text-cyan-500" />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f) => (
                    <tr
                      key={f.ticker}
                      className="border-t border-slate-100 dark:border-slate-800/70 hover:bg-slate-50 dark:hover:bg-slate-900/40"
                      data-testid={`cc-hub-row-${f.ticker}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Link to={`/stock/${f.ticker}`} className="font-mono font-bold text-cyan-700 dark:text-cyan-400 hover:underline">
                            {f.ticker}
                          </Link>
                          {f.leveraged && (
                            <span
                              className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 ring-1 ring-amber-300/60 dark:ring-amber-500/40"
                              title={`${f.leverage_x}× daily leveraged exposure to ${f.leverage_underlying} — does not sell options itself`}
                              data-testid={`cc-hub-leveraged-${f.ticker}`}
                            >
                              {f.leverage_x}× LVG
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[260px]">{f.name}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{fmtPct(f.dividend_yield)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-300 whitespace-nowrap">{fmtMoney(f.dist_ttm)}</td>
                      <td className="px-3 py-2.5 text-center text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{f.frequency || '—'}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtPct(f.expense_ratio, 2)}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold whitespace-nowrap ${f.nav_change_1y == null ? 'text-slate-400' : f.nav_change_1y < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {fmtPct(f.nav_change_1y, 1, true)}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <TotalReturnCell value={f.total_return_1y} />
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtAum(f.net_assets)}</td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr><td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500" data-testid="cc-hub-empty">No funds match that filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              Total return = 1-year share-price change plus every distribution paid in the trailing 12 months,
              measured against the price 12 months ago. Funds younger than a year show “—”. Distribution data
              comes from our own payout history, not the fund's marketing material.
              {asOf && <> Prices and performance as of <strong>{fmtDate(asOf)}</strong>.</>}
            </p>
          </section>

          {/* Education */}
          <section className="mt-12 grid md:grid-cols-3 gap-4" data-testid="cc-hub-education">
            <EduCard
              icon={TrendingDown}
              title="Why a 100% yield can lose money"
              body="Option-income funds sell calls on a volatile underlying and pay the premium out. When the underlying rips higher, the fund's upside is capped but the NAV still drops on the down days. Distribute more than the strategy earns and the payout is funded from capital — the share price grinds down while the yield stays eye-catching."
            />
            <EduCard
              icon={TrendingUp}
              title="Total return is the only honest scoreboard"
              body={`Take the price a year ago, add every distribution you received, compare to today. That's it. A fund paying 12% with a flat price beat a fund paying 130% that fell 82%. Our table does that arithmetic for all ${data?.count || 'tracked'} funds so you don't have to trust anyone's fact sheet.`}
            />
            <EduCard
              icon={AlertTriangle}
              title="Weekly payers are not automatically better"
              body="Weekly distributions feel great and help if you spend the income, but they don't create return — they just slice the same pie thinner. What matters is whether the total distribution is covered by option premium, and what the fee is. Above 1% a year, the manager is taking a big cut of your premium."
            />
          </section>

          {/* CTA */}
          <section className="mt-12 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="cc-hub-cta">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Put a number on it</h2>
            <div className="flex flex-wrap gap-3">
              <Link to="/dividend-reinvestment-calculator" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="cc-hub-cta-calc">
                <Calculator className="w-4 h-4" /> Project a DRIP
              </Link>
              <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="cc-hub-cta-screener">
                <Search className="w-4 h-4" /> Screen quality dividend payers
              </Link>
              <Link to="/learn/covered-call-etfs" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="cc-hub-cta-learn">
                <BookOpen className="w-4 h-4" /> Covered call ETF guide
              </Link>
            </div>
          </section>

          <HubFaq />
        </>
      )}

      <SiteDisclaimer />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function StatTile({ label, value, tone, hint, testId }) {
  const toneCls = tone === 'danger'
    ? 'text-rose-600 dark:text-rose-400'
    : tone === 'warn'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-slate-900 dark:text-slate-100';
  return (
    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl font-black tabular-nums ${toneCls}`}>{value ?? '—'}</div>
      {hint && <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">{hint}</div>}
    </div>
  );
}

function FamilyChip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active
        ? 'bg-cyan-600 border-cyan-600 text-white'
        : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-cyan-400'}`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function EduCard({ icon: Icon, title, body }) {
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800">
      <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100 font-bold">
        <Icon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> {title}
      </div>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}

const HUB_FAQS = [
  {
    q: 'What is a covered call ETF?',
    a: 'A covered call (or option income) ETF holds stocks or an index and sells call options against that position. The option premium is collected and paid out as a distribution, which is why these funds advertise very high yields. The trade-off is capped upside: when the underlying rallies hard, the fund gives up most of the gain.',
  },
  {
    q: 'Which covered call ETF has the highest yield?',
    a: 'Single-stock YieldMax funds such as MSTY, SMCY and CONY carry the highest headline distribution yields, frequently above 80% annualised. Sort the table by the Yield column to see the current ranking — but check the 1-year total return column next to it, because the highest-yielding funds are usually the ones with the largest share-price declines.',
  },
  {
    q: 'Do covered call ETFs lose value over time?',
    a: 'Many do. If a fund distributes more than the option strategy earns, the excess is paid out of capital and the share price drifts down. Our table shows the 1-year price change for every fund so you can see exactly which ones eroded and by how much.',
  },
  {
    q: 'Is a high distribution yield the same as a return?',
    a: 'No. Return is the price change plus the distributions you received. A fund can pay a 130% distribution and still deliver a negative total return if the share price fell more than the distributions paid. Total return is the only fair way to compare these funds.',
  },
  {
    q: 'Are YieldMax ETFs better than JEPI or JEPQ?',
    a: 'They are different products. YieldMax funds run a synthetic covered call on a single volatile stock, producing enormous but unstable distributions and much higher fees (typically about 1% a year). JEPI and JEPQ hold a diversified equity portfolio with an options overlay, charge 0.35% and pay far less, but have historically produced steadier total returns. Compare them directly on our head-to-head pages.',
  },
  {
    q: 'How often is this data updated?',
    a: 'Prices, distributions and performance are refreshed by our nightly data pipeline, so the table reflects the most recent close and the latest declared distributions.',
  },
];

function HubFaq() {
  return (
    <section className="mt-12" data-testid="cc-hub-faq">
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
      <div className="space-y-3">
        {HUB_FAQS.map((f, i) => (
          <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`cc-hub-faq-item-${i}`}>
            <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
              <span>{f.q}</span>
              <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
